/**
 * Time Machine #114 Phase 4.5 — request-body builder for the Lambda method
 * `getStateAndEventsForDate` (sibling to `buildTimeTravelRequestBody` for the
 * legacy Phase 3.5 `getStateAtTimestamp`).
 *
 * Returns the POST body that bundles a baseline state at 00:00 UTC of the
 * requested date PLUS every EventLog entry for that date. The frontend caches
 * both, then "scrubs" through the day in memory via `replayState` (zero new
 * API calls per slider position).
 *
 * Constraints (validated and enforced):
 *   - `targetCollection` must be Attributes / ActiveQueue / Candidates.
 *     Offices time-travel is not implemented (Phase 4 architectural decision).
 *   - `database` must be "Warehouses" (PROD) or "UAT". NOTE (#121): Lambda
 *     `v05-09-2026-04-QRY01`+ overrides this to "Warehouses" for all Time
 *     Machine read methods regardless of value passed (Time Machine has
 *     only one source of truth — PROD).
 *   - `location` is REQUIRED for all 3 collections (6 MB API Gateway response cap).
 *   - `date` must be a strict YYYY-MM-DD string (no time component, no timezone).
 *     If a Date object is passed, it's converted to its UTC date string.
 */

const VALID_TARGET_COLLECTIONS = ["Attributes", "ActiveQueue", "Candidates"];
const VALID_DATABASES = ["Warehouses", "UAT"];

export function buildStateAndEventsRequestBody({
  targetCollection,
  database,
  date,
  location,
} = {}) {
  if (!VALID_TARGET_COLLECTIONS.includes(targetCollection)) {
    throw new Error(
      `buildStateAndEventsRequestBody: targetCollection must be one of ${VALID_TARGET_COLLECTIONS.join(", ")} (got ${JSON.stringify(targetCollection)})`
    );
  }
  if (!VALID_DATABASES.includes(database)) {
    throw new Error(
      `buildStateAndEventsRequestBody: database must be one of ${VALID_DATABASES.join(", ")} (got ${JSON.stringify(database)})`
    );
  }
  if (!location || typeof location !== "string" || location.trim() === "") {
    throw new Error(
      `buildStateAndEventsRequestBody: location required for ${targetCollection} (6 MB API Gateway response cap)`
    );
  }
  if (date == null) {
    throw new Error("buildStateAndEventsRequestBody: date is required");
  }

  // Normalize to YYYY-MM-DD string.
  let dateStr;
  if (date instanceof Date) {
    if (isNaN(date.getTime())) {
      throw new Error("buildStateAndEventsRequestBody: date Date is invalid");
    }
    // Use UTC components so we hit the same day the Lambda will compute.
    const yyyy = date.getUTCFullYear();
    const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(date.getUTCDate()).padStart(2, "0");
    dateStr = `${yyyy}-${mm}-${dd}`;
  } else if (typeof date === "string") {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      throw new Error(
        `buildStateAndEventsRequestBody: date string must be strict YYYY-MM-DD (got ${JSON.stringify(date)})`
      );
    }
    dateStr = date;
  } else {
    throw new Error(
      `buildStateAndEventsRequestBody: date must be a Date or a YYYY-MM-DD string (got ${typeof date})`
    );
  }

  return {
    method: "getStateAndEventsForDate",
    database,
    collection: "AuditSnapshots",
    targetCollection,
    location,
    date: dateStr,
  };
}

export const __testables__ = {
  VALID_TARGET_COLLECTIONS,
  VALID_DATABASES,
};
